import i18n from "../../i18n";
import { Me } from "../../types/api-schema";
import { BROADCAST_MSG_LOGOUT } from "../../utils/constants";
import fetchMe from "../../utils/fetchMe";
import { User } from "../app/propTypes";

import type { AppAction, AppDispatch, AppThunk, RootState } from "../store";

export const OidcActionType = {
  SetBroadcast: "SET_BROADCAST",
  SetMe: "SET_ME",
  SetUser: "SET_USER",
} as const;
export type OidcActionType =
  (typeof OidcActionType)[keyof typeof OidcActionType];

export const SET_USER = OidcActionType.SetUser;
export const SET_ME = OidcActionType.SetMe;
export const SET_BROADCAST = OidcActionType.SetBroadcast;

/** Signature of `redux-thunk`'s `getState` for this app. */
export type GetState = () => RootState;

/** Signature of `redux-thunk`'s `dispatch` for this app. */
export type Dispatch = AppDispatch;

export const setUser = (data: User): AppAction => {
  return {
    data,
    type: SET_USER,
  };
};

export const setMe = (data: Me): AppAction => {
  return {
    data,
    type: SET_ME,
  };
};

export const refreshMe = (): AppThunk => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const { app, oidc } = getState();
    if (!app.meUrl || !oidc?.me) {
      return;
    }
    const me = await fetchMe(app.meUrl);
    if (me) {
      dispatch(setMe(me));
    }
  };
};

export const setBroadcast = (data: BroadcastChannel): AppAction => {
  return {
    data,
    type: SET_BROADCAST,
  };
};

export const login = (
  options: { loginHint?: string; next?: string; prompt?: "create" } = {},
): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { loginUrl },
    } = getState();
    let url = loginUrl;
    if (/next=$/.test(loginUrl)) {
      url = loginUrl + encodeURIComponent(window.location.href);
    }
    const redirect = new URL(url, window.location.href);
    if (options.next) {
      redirect.searchParams.set("next", options.next);
    }
    if (options.loginHint) {
      redirect.searchParams.set("login_hint", options.loginHint);
    }
    if (options.prompt) {
      redirect.searchParams.set("prompt", options.prompt);
    }
    const language = (i18n.resolvedLanguage || i18n.language || "de")
      .toLowerCase()
      .split("-")[0];
    redirect.searchParams.set("ui_locales", language);
    window.location.href = redirect.toString();
  };
};

export const logout = (): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { logoutUrl },
      oidc: { broadcast },
    } = getState();

    if (broadcast) {
      broadcast.postMessage(BROADCAST_MSG_LOGOUT);
    }
    window.location.href = logoutUrl;
  };
};
